import { EditorContent, useEditor } from '@tiptap/react'
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { FILE_ACCEPT, IMAGE_ACCEPT, MAX_ATTACHMENTS, attachmentProblem, kindOf } from '../../../shared/attachments.js'
import { MAX_MENTIONS } from '../../../shared/mentions.js'
import { errorMessage } from '../../lib/api.js'
import { uploadFile } from '../../lib/uploads.js'
import { Icon } from '../icons/Icon.jsx'
import { Menu } from '../ui/Menu.jsx'
import { AttachmentChip } from './AttachmentChip.jsx'
import { MentionPicker } from './MentionPicker.jsx'
import { composerExtensions, mentionSlugs, serializeQuestion } from './extensions.js'
import styles from './Composer.module.css'

const PICKER_WIDTH = 372
const HINT = 'Type @ to tag a system; with no tag, every system is searched. Use + to attach images, PDFs or text files.'

const hasFiles = (event) => [...(event.dataTransfer?.types ?? [])].includes('Files')

function filterSystems(systems, query) {
  const q = String(query ?? '').trim().toLowerCase()
  const list = systems ?? []
  if (!q) return list
  return list.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.shortName?.toLowerCase().includes(q) ||
      s.slug.includes(q),
  )
}

function firstEnabled(items, added, from = 0, step = 1) {
  const n = items.length
  for (let i = 0; i < n; i++) {
    const index = (((from + i * step) % n) + n) % n
    const s = items[index]
    const full = !added.includes(s.slug) && added.length >= MAX_MENTIONS
    if (s.status !== 'none' && !full) return index
  }
  return -1
}

/**
 * Question composer (TipTap) with optional inline @system tags. Typing @ opens
 * a picker that filters as you type; tagged systems are answered from first,
 * and with no tag the server searches every system's skill.
 *
 * Files: the + menu (Upload image / Upload file), paste or drag-and-drop.
 * Each file uploads straight away; Send waits until all have uploaded and
 * passes them to onSubmit as `attachments` (uploads from POST /api/uploads).
 *
 * Imperative handle: focus(), setQuestion({ text }), clear().
 */
export const Composer = forwardRef(function Composer(
  { placeholder, systems, onSubmit, busy = false, onStop, autoFocus = false },
  ref,
) {
  const wrapRef = useRef(null)
  const listId = useId()
  const systemsMap = useMemo(() => new Map((systems ?? []).map((s) => [s.slug, s])), [systems])
  const systemsRef = useRef(systemsMap)
  systemsRef.current = systemsMap

  const [picker, setPickerState] = useState(null) // { query, range, command, left }
  const [highlighted, setHighlightedState] = useState(0)
  const [docState, setDocState] = useState({ empty: true, mentions: [] })

  const items = useMemo(() => filterSystems(systems, picker?.query), [systems, picker?.query])

  // TipTap callbacks are created once and can fire between renders, so the
  // picker state they read lives in refs that are updated synchronously.
  const pickerRef = useRef(null)
  const highlightRef = useRef(0)
  const systemsListRef = useRef(systems)
  systemsListRef.current = systems
  const setPicker = useCallback((value) => {
    pickerRef.current = value
    setPickerState(value)
  }, [])
  const setHighlighted = useCallback((value) => {
    highlightRef.current = value
    setHighlightedState(value)
  }, [])

  // Attached files: { key, file, name, size, kind, status, error, upload, preview }.
  // The ref is updated synchronously so several adds before a render all count.
  const [files, setFilesState] = useState([])
  const filesRef = useRef([])
  const setFiles = useCallback((update) => {
    const next = typeof update === 'function' ? update(filesRef.current) : update
    filesRef.current = next
    setFilesState(next)
  }, [])
  const [notice, setNotice] = useState(null)
  const [dragging, setDragging] = useState(false)
  const inFlight = useRef(new Map()) // key → AbortController
  const imageInput = useRef(null)
  const fileInput = useRef(null)

  const patchFile = useCallback(
    (key, patch) => setFiles((list) => list.map((f) => (f.key === key ? { ...f, ...patch } : f))),
    [setFiles],
  )

  const startUpload = useCallback(
    (key, file) => {
      const controller = new AbortController()
      inFlight.current.set(key, controller)
      patchFile(key, { status: 'uploading', error: null })
      uploadFile(file, { signal: controller.signal })
        .then((upload) => patchFile(key, { status: 'ready', upload }))
        .catch((error) => {
          if (error?.name !== 'AbortError') patchFile(key, { status: 'failed', error: errorMessage(error) })
        })
        .finally(() => inFlight.current.delete(key))
    },
    [patchFile],
  )

  const addFiles = useCallback(
    (fileList) => {
      const incoming = [...(fileList ?? [])]
      if (!incoming.length) return
      const room = MAX_ATTACHMENTS - filesRef.current.length
      const problems = []
      const accepted = []
      for (const file of incoming) {
        const problem = attachmentProblem(file.name || 'pasted-image.png', file.type, file.size)
        if (problem) problems.push(problem)
        else if (accepted.length < room) accepted.push(file)
      }
      if (incoming.length - problems.length > room) problems.push(`Only ${MAX_ATTACHMENTS} files can go with one question.`)
      setNotice(problems.length ? problems.join(' ') : null)
      const added = accepted.map((file) => {
        const name = file.name || 'pasted-image.png'
        const kind = kindOf(name, file.type)
        return { key: crypto.randomUUID(), file, name, size: file.size, kind, status: 'uploading', preview: kind === 'image' ? URL.createObjectURL(file) : null }
      })
      setFiles((list) => [...list, ...added])
      added.forEach((f) => startUpload(f.key, f.file))
    },
    [setFiles, startUpload],
  )
  const addFilesRef = useRef(addFiles)
  addFilesRef.current = addFiles

  const removeFile = useCallback(
    (key) => {
      inFlight.current.get(key)?.abort()
      const file = filesRef.current.find((f) => f.key === key)
      if (file?.preview) URL.revokeObjectURL(file.preview)
      setFiles((list) => list.filter((f) => f.key !== key))
      setNotice(null)
    },
    [setFiles],
  )

  const clearFiles = useCallback(() => {
    filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview))
    setFiles([])
  }, [setFiles])

  // Leaving the page: stop uploads and free the previews.
  useEffect(
    () => () => {
      inFlight.current.forEach((controller) => controller.abort())
      filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview))
    },
    [],
  )

  // Live props for callbacks.
  const live = useRef({})
  live.current = { busy, onSubmit }
  const editorRef = useRef(null)
  const lastQuery = useRef(null)

  const pick = useCallback(
    (system) => {
      const current = pickerRef.current
      const editor = editorRef.current
      if (!current || !editor || system.status === 'none') return
      const added = mentionSlugs(editor)
      if (added.includes(system.slug)) {
        editor.chain().focus().deleteRange(current.range).run()
      } else if (added.length >= MAX_MENTIONS) {
        return
      } else {
        current.command({ id: system.slug, label: system.name })
      }
      setPicker(null)
    },
    [setPicker],
  )

  const submit = useCallback(() => {
    const editor = editorRef.current
    const { busy: isBusy, onSubmit: handler } = live.current
    if (!editor || isBusy) return true
    const attached = filesRef.current
    if (attached.some((f) => f.status === 'uploading')) {
      setNotice('Wait for the files to finish uploading, then send.')
      return true
    }
    if (attached.some((f) => f.status === 'failed')) {
      setNotice('Remove the files that failed to upload, or try them again.')
      return true
    }
    const text = serializeQuestion(editor)
    if (!text && !attached.length) return true
    const attachments = attached.map((f) => f.upload)
    if (handler?.({ text, mentions: mentionSlugs(editor), attachments }) !== false) {
      editor.commands.clearContent(true)
      clearFiles()
      setNotice(null)
    }
    return true
  }, [clearFiles])

  const suggestion = useMemo(
    () => ({
      char: '@',
      allowSpaces: false,
      render: () => {
        const open = (props) => {
          const wrap = wrapRef.current?.getBoundingClientRect()
          const caret = props.clientRect?.()
          const left = wrap && caret ? Math.max(0, Math.min(caret.left - wrap.left - 24, wrap.width - PICKER_WIDTH)) : 0
          if (lastQuery.current !== props.query) {
            lastQuery.current = props.query
            setHighlighted(-1) // the effect below moves it to the first enabled row
          }
          setPicker({ query: props.query, range: props.range, command: props.command, left })
        }
        return {
          onStart: open,
          onUpdate: open,
          onExit: () => {
            lastQuery.current = null
            setPicker(null)
          },
          onKeyDown: ({ event }) => {
            const current = pickerRef.current
            if (!current) return false
            const list = filterSystems(systemsListRef.current, current.query)
            const added = mentionSlugs(editorRef.current)
            let index = highlightRef.current
            if (index < 0 || index >= list.length) index = firstEnabled(list, added, 0, 1)
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              const step = event.key === 'ArrowDown' ? 1 : -1
              const next = firstEnabled(list, added, index + step, step)
              if (next !== -1) setHighlighted(next)
              return true
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
              // No matching system: let Enter send the text as typed.
              if (index === -1 || !list[index]) return false
              pick(list[index])
              return true
            }
            if (event.key === 'Escape') {
              setPicker(null)
              return true
            }
            return false
          },
        }
      },
    }),
    [pick, setPicker, setHighlighted],
  )

  // Everything TipTap holds is created once; callbacks read live values via refs.
  const extensions = useMemo(
    () =>
      composerExtensions({
        getSystem: (slug) => systemsRef.current.get(slug),
        placeholder: placeholder ?? 'Ask a follow-up — type @ to tag a system',
        suggestion,
        onSubmit: () => submit(),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const editorProps = useMemo(
    () => ({
      attributes: {
        class: styles.editor,
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Ask a question',
        'aria-controls': listId,
      },
      // Pasted or dropped files become attachments instead of editor content.
      handlePaste: (_view, event) => {
        const pasted = [...(event.clipboardData?.files ?? [])]
        if (!pasted.length) return false
        addFilesRef.current(pasted)
        return true
      },
      handleDrop: (_view, event) => {
        const dropped = [...(event.dataTransfer?.files ?? [])]
        if (!dropped.length) return false
        addFilesRef.current(dropped)
        return true
      },
    }),
    [listId],
  )

  const editor = useEditor({
    extensions,
    autofocus: autoFocus ? 'end' : false,
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    editorProps,
    onUpdate: ({ editor: e }) => setDocState({ empty: e.isEmpty, mentions: mentionSlugs(e) }),
  })
  editorRef.current = editor

  // Keep the highlight on an enabled row as the query or tags change.
  useEffect(() => {
    if (!picker) return
    if (highlighted < 0 || highlighted >= items.length) {
      setHighlighted(Math.max(0, firstEnabled(items, docState.mentions, 0, 1)))
    }
  }, [picker, items, highlighted, docState.mentions, setHighlighted])

  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor?.commands.focus('end'),
      clear: () => editor?.commands.clearContent(true),
      setQuestion: ({ text = '' }) => {
        if (!editor) return
        editor.commands.setContent({ type: 'doc', content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }] }, { emitUpdate: true })
        editor.commands.focus('end')
      },
    }),
    [editor],
  )

  return (
    <div className={styles.wrap}>
      <div ref={wrapRef} className={styles.anchor}>
        {picker ? (
          <MentionPicker
            items={items}
            highlighted={highlighted}
            added={docState.mentions}
            onPick={pick}
            onHover={setHighlighted}
            listId={listId}
            style={{ left: picker.left }}
          />
        ) : null}
        <div
          className={`${styles.box} ${dragging ? styles.boxDragging : ''}`}
          onDragEnter={(e) => {
            if (!hasFiles(e)) return
            e.preventDefault()
            setDragging(true)
          }}
          onDragOver={(e) => {
            if (!hasFiles(e)) return
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false)
          }}
          onDrop={(e) => {
            setDragging(false)
            if (e.defaultPrevented || !hasFiles(e)) return
            e.preventDefault()
            addFiles(e.dataTransfer.files)
          }}
        >
          {files.length ? (
            <ul className={styles.tray} aria-label="Attached files">
              {files.map((f) => (
                <AttachmentChip
                  key={f.key}
                  file={f}
                  onRemove={() => removeFile(f.key)}
                  onRetry={() => startUpload(f.key, f.file)}
                />
              ))}
            </ul>
          ) : null}
          <EditorContent editor={editor} />
          <div className={styles.row}>
            <Menu
              side="top"
              align="start"
              trigger={(props) => (
                <button
                  type="button"
                  {...props}
                  className={styles.attach}
                  aria-label="Attach images or files"
                  title="Attach images or files"
                  disabled={files.length >= MAX_ATTACHMENTS}
                >
                  <Icon name="plus" size={18} strokeWidth={1.8} />
                </button>
              )}
              items={[
                { label: 'Upload image', onSelect: () => imageInput.current?.click() },
                { label: 'Upload file', onSelect: () => fileInput.current?.click() },
              ]}
            />
            <input
              ref={imageInput}
              type="file"
              accept={IMAGE_ACCEPT}
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files)
                e.target.value = ''
              }}
            />
            <input
              ref={fileInput}
              type="file"
              accept={FILE_ACCEPT}
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files)
                e.target.value = ''
              }}
            />
            {busy && onStop ? (
              <button type="button" className={styles.send} aria-label="Stop answering" onClick={onStop}>
                <Icon name="stop" size={16} strokeWidth={1.8} />
              </button>
            ) : (
              <button
                type="button"
                className={styles.send}
                aria-label="Send question"
                disabled={(docState.empty && !files.length) || busy || files.some((f) => f.status !== 'ready')}
                onClick={() => submit()}
              >
                <Icon name="arrow-up" size={16} strokeWidth={1.8} />
              </button>
            )}
          </div>
        </div>
      </div>
      {notice ? (
        <p className={styles.notice} role="alert">
          {notice}
        </p>
      ) : null}
      <p className={styles.hint}>{HINT}</p>
    </div>
  )
})
